import { useEffect, useState } from 'react';
import { srpFilterInitialValues } from 'redux-implementations/srpFilters';
import { useTranslation } from 'react-i18next';
import {
  CheckboxValue,
  FilterPill,
  FilterValues,
  SrpCategoryValue,
  SrpColorsFilterValue,
  SrpFilterName,
  SrpFilterRangeValue,
  SrpFilterValues,
  SrpRadiusFilterValue,
  SrpTruckOptionsFilterValue,
} from 'types/srpFilters';
import { createFilterPillFunctions } from '../../components/Srp/SearchBar/Mobile/getFilterPills';

export default function useGetFilterPills(filters: SrpFilterValues): FilterPill[] {
  const { t } = useTranslation();

  function checkBoolean(filter: CheckboxValue): boolean {
    let booleanFlag = false;
    Object.keys(filter).forEach((key: string) => {
      if (filter[key]) {
        booleanFlag = true;
      }
    });
    return booleanFlag;
  }

  const [filterPills, setFilterPills] = useState<FilterPill[]>([]);

  useEffect(() => {
    function filterHasDefaultValue(filterName: SrpFilterName, filterValues: FilterValues): boolean {
      switch (filterName) {
        case 'Condition':
        case 'Doors':
        case 'MakeModel':
        case 'Trims':
        case 'Category': {
          const category = filterValues as SrpCategoryValue;
          return category === srpFilterInitialValues[filterName];
        }
        case 'Advanced':
        case 'Cylinders':
        case 'Transmission':
        case 'Drivetrain':
        case 'Powertrain':
        case 'BodyTypes':
        case 'Location':
        case 'FeaturesTechnology':
          return !checkBoolean(filterValues as CheckboxValue);
        case 'Year':
        case 'Mileage':
        case 'Price': {
          const priceRange = filterValues as SrpFilterRangeValue;
          return (
            priceRange.min === srpFilterInitialValues[filterName].min &&
            priceRange.max === srpFilterInitialValues[filterName].max
          );
        }
        case 'TruckOptions': {
          const values = filterValues as SrpTruckOptionsFilterValue;
          return (
            !checkBoolean(values.cabinType as unknown as CheckboxValue) &&
            !checkBoolean(values.rearWheel as unknown as CheckboxValue) &&
            !checkBoolean(values.bedType as unknown as CheckboxValue) &&
            values.bedLength.min === 3 &&
            values.bedLength.max === 9
          );
        }
        case 'Colors': {
          const values = filterValues as SrpColorsFilterValue;
          return (
            !checkBoolean(values.exterior as unknown as CheckboxValue) &&
            !checkBoolean(values.interior as unknown as CheckboxValue)
          );
        }
        case 'Radius': {
          const values = filterValues as SrpRadiusFilterValue;
          return Number(values) === srpFilterInitialValues[SrpFilterName.Radius];
        }
        case 'SortBy':
          return !filterValues;
        case 'FuelEfficiency':
          return (filterValues as number) === 0;
        default:
          return true;
      }
    }
    function getFilterPills(): void {
      const generatedFilterPills = Object.keys(filters).reduce((pills: FilterPill[], filter) => {
        const filterName = filter as SrpFilterName;
        const filterValues = filters[filterName] as FilterValues;
        if (!filterHasDefaultValue(filterName, filterValues)) {
          const createFilterPillFunc = createFilterPillFunctions(t)[filterName];
          const createdFilterPill = createFilterPillFunc(filters[filterName] as never);
          if (createdFilterPill) {
            pills.push(...(createdFilterPill as FilterPill[]));
          }
        }
        return pills;
      }, []) as unknown as FilterPill[];
      return setFilterPills(generatedFilterPills);
    }
    getFilterPills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return filterPills;
}
